import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link, NavigationType, useLocation, useNavigationType } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, ChevronDown, Menu, X } from 'lucide-react';

import websiteLogo from '@/assets/website/flyst-logo.svg';
import { cx } from '@/lib/cx';
import styles from './PublicCareersLayout.module.css';

const WEBSITE_URL = 'https://www.flyst.co.in';
const WEBSITE_LINKS = [
  ['Company', '/company/'],
  ['Services', '/services/'],
  ['Candidates', '/candidates/'],
  ['Employers', '/employers/'],
] as const;
const RESOURCE_LINKS = [
  ['Press Releases', '/press-releases/'],
  ['Blogs', '/blog/'],
  ['Events', '/events/'],
  ['FAQ', '/faq/'],
] as const;

export function PublicCareersLayout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const navId = useId();
  const mainId = useId();
  const menuButton = useRef<HTMLButtonElement>(null);
  const resources = useRef<HTMLDetailsElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    // A job opened from a scrolled catalogue should start at its title.
    // Leave browser back/forward restoration alone.
    if (navigationType !== NavigationType.Pop) window.scrollTo(0, 0);
  }, [pathname, navigationType]);
  const onNavigationKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape') return;
    if (resources.current?.open) {
      resources.current.open = false;
      resources.current.querySelector('summary')?.focus();
    } else if (menuOpen) {
      setMenuOpen(false);
      menuButton.current?.focus();
    }
  };

  return (
    <div className={styles.layout}>
      <a className={styles.skipLink} href={`#${mainId}`}>
        Skip to jobs content
      </a>
      <div className={styles.topbar}>
        <span>OVERSEAS CAREERS, WITH PERSONAL GUIDANCE.</span>
        <a href="tel:+917439991747">+91 74399 91747</a>
      </div>
      <header className={styles.header} onKeyDown={onNavigationKeyDown}>
        <a href={`${WEBSITE_URL}/`} aria-label="Flyst Overseas home" className={styles.brand}>
          <img src={websiteLogo} width={143} height={58} alt="Flyst Overseas" />
        </a>
        <button
          ref={menuButton}
          type="button"
          className={styles.menuToggle}
          aria-expanded={menuOpen}
          aria-controls={navId}
          onClick={() => {
            setMenuOpen((open) => !open);
          }}
        >
          {menuOpen ? 'Close menu' : 'Menu'}
          {menuOpen ? (
            <X size={20} aria-hidden="true" />
          ) : (
            <Menu size={20} aria-hidden="true" />
          )}
        </button>
        <nav
          id={navId}
          aria-label="Main navigation"
          className={cx(styles.navigation, menuOpen && styles.navigationOpen)}
        >
          {WEBSITE_LINKS.map(([label, path]) => (
            <a key={path} href={`${WEBSITE_URL}${path}`}>
              {label}
            </a>
          ))}
          <details className={styles.resources} ref={resources}>
            <summary>
              Resources <ChevronDown size={16} aria-hidden="true" />
            </summary>
            <div className={styles.resourceLinks}>
              {RESOURCE_LINKS.map(([label, path]) => (
                <a key={path} href={`${WEBSITE_URL}${path}`}>
                  {label}
                  <ArrowUpRight size={15} aria-hidden="true" />
                </a>
              ))}
            </div>
          </details>
          <a href={`${WEBSITE_URL}/contact/`}>Contact</a>
        </nav>
      </header>
      <main id={mainId} className={styles.main}>
        <nav aria-label="Careers breadcrumb" className={styles.breadcrumb}>
          <a href={`${WEBSITE_URL}/`}>
            <ArrowLeft size={15} aria-hidden="true" /> Back to Flyst website
          </a>
          <span aria-hidden="true">/</span>
          <Link to="/jobs">Careers</Link>
        </nav>
        {children}
      </main>
      <footer className={styles.footer}>
        <div className={styles.footerGrid}>
          <div className={styles.footerBrand}>
            <a href={`${WEBSITE_URL}/`} aria-label="Flyst Overseas home">
              <img src={websiteLogo} width={180} height={68} alt="Flyst Overseas" />
            </a>
            <p>
              Rooted in India.
              <br />
              Connected to the world.
            </p>
            <a className={styles.homeLink} href={`${WEBSITE_URL}/`}>
              Visit the Flyst website <ArrowUpRight size={17} aria-hidden="true" />
            </a>
          </div>
          <nav aria-label="Explore Flyst">
            <h2>Explore</h2>
            {WEBSITE_LINKS.map(([label, path]) => (
              <a key={path} href={`${WEBSITE_URL}${path}`}>
                {label}
              </a>
            ))}
            <a href={`${WEBSITE_URL}/destinations/`}>Destinations</a>
          </nav>
          <nav aria-label="Career resources">
            <h2>Resources</h2>
            {RESOURCE_LINKS.map(([label, path]) => (
              <a key={path} href={`${WEBSITE_URL}${path}`}>
                {label}
              </a>
            ))}
            <a href={`${WEBSITE_URL}/book-online/`}>Book a consultation</a>
          </nav>
          <div className={styles.footerContact}>
            <h2>Let’s connect</h2>
            <a href="mailto:info@flyst.co.in">info@flyst.co.in</a>
            <a href="tel:+917439991747">+91 74399 91747</a>
            <a href={`${WEBSITE_URL}/contact/`}>Contact our team</a>
            <p>
              Monday to Saturday
              <br />
              11:00 AM to 6:00 PM IST
            </p>
          </div>
        </div>
        <div className={styles.footerBottom}>
          <span>© {new Date().getFullYear()} FLYST LLP. All rights reserved.</span>
          <Link to="/jobs">All open jobs</Link>
          <a href={`${WEBSITE_URL}/terms-and-conditions/`}>Terms & privacy</a>
        </div>
      </footer>
    </div>
  );
}
